import { isOrder } from '@/lib/stageUtil'
import { AttendanceMutator } from '@/lib/entity/attendance'
import { AssignMutator } from '@/lib/entity/assign'
import { AssignPlanMutator } from '@/lib/entity/assign_plan'
import { CustomFieldChoiceMutator } from '@/lib/entity/custom_field_choice'
import { ProjectMutator } from '@/lib/entity/project'
import { RegularHolidayMutator } from '@/lib/entity/regular_holiday'
import { WorkOnProjectMutator } from '@/lib/entity/work_on_project'
import { VolumedAssignMutator } from '@/lib/entity/volumed_assign'
import { VolumedAssignPlanMutator } from '@/lib/entity/volumed_assign_plan'
import { TenantMutator } from '@/lib/entity/tenant'
import { TargetCostOtherMutator } from '@/lib/entity/target_cost_other'
import { TargetCostOtherActualMutator } from '@/lib/entity/target_cost_other_actual'
import { convertCardFromEntity } from '@/lib/converter/base'
import type { EntityCard } from '@/lib/converter/base'
import type { Stageable } from '@/lib/entity/base'
import i18next from 'i18next'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'

export type stageCard = {
  EntityCard: EntityCard
  stageKeys: string[]
}

const createCard = async (key: string, stageable: Stageable) => {
  return {
    EntityCard: await convertCardFromEntity(stageable),
    stageKeys: [key]
  }
}

const createOrderCard = (key: string, stageable: Stageable): stageCard => {
  return {
    EntityCard: {
      icon: 'sort',
      title: i18next.t(`stages.table.${stageable.table}`),
      action: i18next.t('stages.actions.edit'),
      subTitle: i18next.t('stages.actions.order'),
      modifiedFields: [],
      isError: false
    },
    stageKeys: [key]
  }
}

const createDeleteCard = async (key: string, stageable: Stageable): Promise<stageCard> => {
  const entityCard = await convertCardFromEntity(stageable)
  return {
    EntityCard: {
      icon: i18next.t(`stages.icon.${stageable.table}`),
      title: i18next.t(`stages.table.${stageable.table}`),
      action: i18next.t('stages.actions.delete'),
      subTitle: entityCard.subTitle,
      modifiedFields: [],
      isError: entityCard.isError
    },
    stageKeys: [key]
  }
}

const getProjectId = async (assignPlanId: string) => {
  const assign = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom('assign')
      .innerJoin('assign_plan', 'assign.id', 'assign_plan.assign_id')
      .select(['assign.project_id'])
      .where('assign_plan.id', '=', assignPlanId)
      .execute()
  })
  return assign[0]?.project_id || ''
}

const getProjectIdFromVolumedAssignPlan = async (planId: string) => {
  const assign = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom('volumed_assign')
      .innerJoin(
        'volumed_assign_plan',
        'volumed_assign.id',
        'volumed_assign_plan.volumed_assign_id'
      )
      .select(['volumed_assign.project_id'])
      .where('volumed_assign_plan.id', '=', planId)
      .execute()
  })
  return assign[0]?.project_id || ''
}

const createStackPath = async (key: string, stage: Stageable): Promise<string> => {
  if (stage instanceof AssignMutator)
    return `/resource_plan/${stage.context.project_id}/${stage.context.id}`
  if (stage instanceof AssignPlanMutator) {
    const projectId = await getProjectId(stage.context.id!)
    return `/resource_plan/${projectId}/${stage.context.id}`
  }
  if (stage instanceof VolumedAssignPlanMutator) {
    const projectId = await getProjectIdFromVolumedAssignPlan(stage.context.id!)
    return `/resource_plan/${projectId}/${stage.context.id}`
  }
  if (stage instanceof AttendanceMutator) return `/worksheet/${stage.context.month}`
  // AttendanceMutator と遷移先が同じだとカードが集約されるので、assign_id をつける
  if (stage instanceof WorkOnProjectMutator)
    return `/worksheet/${stage.context.month}/${stage.context.assign_id}`
  if (stage instanceof ProjectMutator) return `/project/${stage.context.id}`
  if (stage instanceof RegularHolidayMutator) return `/holiday/regular_holiday/${stage.context.id}`
  if (stage instanceof TenantMutator) return `/setting`
  if (stage instanceof VolumedAssignMutator)
    return `/volumedAssign/${stage.context.project_id}/${stage.context.id}`
  if (stage instanceof TargetCostOtherMutator)
    return `/other_cost_edit/${stage.context.project_id}/${stage.context.id}`
  if (stage instanceof TargetCostOtherActualMutator)
    return `/other_cost_edit/${stage.context.project_id}/${stage.context.target_cost_other_id}/${stage.context.id}`

  // Orderが存在しない処理はこれより上に記載する必要あり
  if (stage instanceof CustomFieldChoiceMutator && !isOrder(key)) {
    return `/custom_field/${stage.context.custom_field_id}/${stage.context.id}`
  }
  if (!isOrder(key)) return key
  if (stage instanceof CustomFieldChoiceMutator) {
    return `/custom_field/${stage.context.custom_field_id}/order`
  }
  return key.replace(/\/order\/.*$/, '')
}

const handleCardCreation = async (
  key: string,
  stageable: Stageable
): Promise<[string, stageCard]> => {
  const stackPath = await createStackPath(key, stageable)

  if (stageable.isDeleted) {
    return [stackPath, await createDeleteCard(key, stageable)]
  }

  let card: stageCard
  if (isOrder(key)) {
    card = createOrderCard(key, stageable)
    // 既存のstageKeysにkeyが含まれていない場合、追加する処理
    if (!card.stageKeys.includes(key)) {
      card.stageKeys.push(key)
    }
  } else {
    card = await createCard(key, stageable)
  }

  return [stackPath, card]
}

export const updateStageCardEntity = async (
  stage: Map<string, Stageable>,
  stageCard: Map<string, stageCard>
) => {
  // Promise.allを使用して、各stageableのカード生成処理を並列実行
  const creationResults = await Promise.all(
    Array.from(stage.entries())
      .filter(([, stageable]) => stageable.isModified() || stageable.isDeleted)
      .map(async ([key, stageable]) => {
        return await handleCardCreation(key, stageable)
      })
  )
  //stageCardを洗い替え
  stageCard.clear()
  creationResults.forEach((result) => {
    const [stackPath, card] = result
    stageCard.set(stackPath, card)
  })
}
